import { useCallback, useEffect, useState } from "react"
import { AddWorkflowFormProps } from "./AddScopeWorkflowForm.types"
import WorkflowInfoForm from "./ScopeWorkflowForm/WorkflowInfoForm"
import SectionForm from "./SectionForm/SectionForm"
import OverviewForm from "./OverviewForm/OverviewForm"
import ScopeWorkflowFormType from "../../../../../utils/ScopeWorkflowFormType"
import { CAMERA_OPTICAL_DIAMETER, MAX_SCOPE_WORKFLOW_SECTIONS } from "../../../../../utils/constants"
import useScopeWorkflowInputs from "../../../../../hooks/useScopeWorkflowInputs"

const useScopeAddWorkflow = (props: AddWorkflowFormProps) => {
  const {
    claddingMaterials,
    insulationMaterials,
    locations,
    onSubmit,
    scopeWorkflow,
    showForm,
    formType,
    onHide 
  } = props

  const [currentSection, setCurrentSection] = useState<number>(0)
  const [currentForm, setCurrentForm] = useState<number>(0)
  const [showSectionAngleMenu, setShowSectionAngleMenu] = useState<boolean>(false)

  const [{
    pipeId,
    location,
    pipeLength,
    pipeTemperature,
    pipeDiameter,
    assetType,
    workflowStart,
    workflowEnd,
    insulationMaterial,
    insulationThickness,
    claddingMaterial,
    claddingThickness,
    hasTracingCable,
    sectionStarts,
    sectionEnds,
    sectionAngles,
    sectionType,
    newAngle,
    onAddAngle,
    onRemoveAngle,
    onAddSectionValues,
    setScopeWorklfowForm,
    onRemoveCurrentSectionInputs,
    clearInputs,
    isScopeValidWorkflowInfo,
    isValidSectionForm
  }] = useScopeWorkflowInputs({
    locations: locations,
    insulationMaterials: insulationMaterials,
    claddingMaterials: claddingMaterials,
    currentSection: currentSection
  })

  useEffect(() => { 
    clearInputs()
    setCurrentForm(0)

    switch (formType) {
      case ScopeWorkflowFormType.VIEW:
        if (scopeWorkflow === undefined) { return }
        setCurrentForm(2)
        setScopeWorklfowForm(scopeWorkflow)
        return
      case ScopeWorkflowFormType.EDIT:
        if (scopeWorkflow === undefined) { return }
        setCurrentForm(0)
        setScopeWorklfowForm(scopeWorkflow)
        return
    }
      
    if (scopeWorkflow === undefined) { 
      clearInputs()
      setCurrentForm(0)
      return
   }
    
  }, [showForm, formType, scopeWorkflow, setScopeWorklfowForm, clearInputs])


  const onNextForm = async () => {    
    if (currentForm === 0) {
      if (!isScopeValidWorkflowInfo()) {
        return
      }
      if (sectionStarts.value.length === 0) {
        onAddSection()
      }      
    }

    if (currentForm === 1) {
      if (!isValidSectionForm()) {
        return
      }
    }

    if (currentForm === forms.length - 1 && formType === ScopeWorkflowFormType.VIEW) {
      setCurrentForm(0)
      onHide()
      return
    }

    if (currentForm === forms.length - 1) {
      onSubmit(getWorkflow())
      setCurrentForm(0)
      onHide()
      return
    }

    setCurrentForm(currentForm + 1)
  }


  const onAddSection = () => {
    if (sectionStarts.value.length >= MAX_SCOPE_WORKFLOW_SECTIONS) { return }
    onAddSectionValues()
    setCurrentSection(sectionStarts.value.length)
  }

  const onRemoveCurrentSection = useCallback(() => {  
    if (sectionStarts.value.length === 1) { return }
    setCurrentSection((prev) => (prev === 0 ? 0 : prev-1))
    onRemoveCurrentSectionInputs()
  }, [sectionStarts, onRemoveCurrentSectionInputs])

  const getSections = () => {
    const sections = sectionStarts.value.map((start, i) => {
      return [parseInt(start.value), parseInt(sectionEnds.value[i].value), sectionType.selected]
    })
    return JSON.stringify(sections)
  }

  const getAngles = () => {
    const angles = sectionAngles.map((angles, i) => {
      return angles.map(angle => parseInt(angle.value))
    })
    return JSON.stringify(angles)
  }

  const getWorkflow = () => {
    return {
      id: scopeWorkflow?.id ?? 1,
      pipe_id: pipeId.value,
      user_id: scopeWorkflow?.user_id ?? 0,
      date: `${Math.floor(Date.now() / 1000)}`,
      start: parseInt(workflowStart.value),
      end: parseInt(workflowEnd.value),
      cladding_material: claddingMaterial.values[claddingMaterial.selected],
      cladding_thickness: parseInt(claddingThickness.value),
      insulation_material: insulationMaterial.values[insulationMaterial.selected],
      insulation_thickness: parseInt(insulationThickness.value),
      pipe_diameter: parseInt(pipeDiameter.value),
      pipe_length: parseInt(pipeLength.value),
      pipe_temp: parseInt(pipeTemperature.value),
      asset_type: 0,
      location: location.values[location.selected],
      has_tracing_cable: hasTracingCable.selected === 0 ? true : false,
      sections: getSections(),
      angles: getAngles(),
      is_fully_uploaded: false
    }
  }

  const onSelectSection = (section: number) => {
    if (formType === ScopeWorkflowFormType.VIEW) { return }
    setCurrentForm(section)
  }

  const onAddFourAngleScan = () => {
    onAddAngle({ value: "0", error: null})
    onAddAngle({ value: "90", error: null})
    onAddAngle({ value: "180", error: null})
    onAddAngle({ value: "270", error: null})
    setShowSectionAngleMenu(false)
  }

  const onAddEightAngleScan = () => {
    onAddAngle({ value: "0", error: null})
    onAddAngle({ value: "45", error: null})
    onAddAngle({ value: "90", error: null})
    onAddAngle({ value: "135", error: null})
    onAddAngle({ value: "180", error: null})
    onAddAngle({ value: "225", error: null})
    onAddAngle({ value: "270", error: null})
    onAddAngle({ value: "315", error: null})
    setShowSectionAngleMenu(false)
  }

  const addDefaultAngles = () => {
    if (isNaN(parseInt(pipeDiameter.value))) { return }

    let numberOfAngles = Math.ceil((Math.PI * parseInt(pipeDiameter.value)) / CAMERA_OPTICAL_DIAMETER)

    const defaultAngles: number[] = []
    const increment = 360 / numberOfAngles    

    for (let i=0; i<numberOfAngles; i++) {      
      defaultAngles.push(Math.round(i*increment))
    }

    for (let i=0; i<defaultAngles.length; i++) {
      onAddAngle({ value: `${defaultAngles[i]}`, error: null})
    }
    setShowSectionAngleMenu(false)
  }

  const forms = [
    <WorkflowInfoForm 
      pipeId={pipeId}
      location={location}
      pipeLength={pipeLength}
      pipeTemperature={pipeTemperature}
      pipeDiameter={pipeDiameter}
      assetType={assetType}
      workflowStart={workflowStart}
      workflowEnd={workflowEnd}
      insulationMaterial={insulationMaterial}
      insulationThickness={insulationThickness}
      claddingMaterial={claddingMaterial}
      claddingThickness={claddingThickness}
      hasTracingCable={hasTracingCable}
      onSubmit={onNextForm}
    />,
    <SectionForm 
      sectionTypes={sectionType}
      sectionStarts={sectionStarts.value}
      onChangeSectionStarts={sectionStarts.onChange}
      sectionEnds={sectionEnds.value}
      onChangeSectionEnds={sectionEnds.onChange}
      sectionAngles={sectionAngles}
      newAngle={newAngle}
      onAddAngle={onAddAngle}
      onRemoveAngle={onRemoveAngle}
      currentSection={currentSection}
      onAddSection={onAddSection}
      onRemoveCurrentSection={onRemoveCurrentSection}
      onSelectSection={(section: number) => setCurrentSection(section)}
      sectionAnglesEllipsisMenu={[
        { item: "Default angles", onClick: addDefaultAngles},
        { item: "4 point star", onClick: onAddFourAngleScan},
        { item: "8 point star", onClick: onAddEightAngleScan},
      ]}
      sectionAnglesEllipsisMenuState={{ value: showSectionAngleMenu, onChange: setShowSectionAngleMenu}}
    />,
    <OverviewForm workflow={getWorkflow()} />
  ]

  return [{
    currentForm: forms[currentForm],
    forms: ["WorkflowInfoForm", "SectionForm", "Overview"],
    currentFormIndex: currentForm,
    onSelectForm: onSelectSection,
    onNext: onNextForm,
  }]
}

export default useScopeAddWorkflow